import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import "mdb-ui-kit/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Input, Ripple, initMDB } from "mdb-ui-kit";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMediaQuery, useTheme } from "@mui/material";
import FooterManual from "./footerManual";
import Notes from "./header";

initMDB({ Input, Ripple, initMDB });

// Constants for Color Palette - RED/LIGHT RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";
const LIGHT_RED_BG = "#FEF2F2";

// Styled Paper Component
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

// Helper component for the Diamond Grid Feature
const DiamondFeature = ({ title, icon, details }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Box
      sx={{
        textAlign: "center",
        position: "relative",
        padding: isMobile ? "12px" : isTablet ? "16px" : "20px",
        marginBottom: isMobile ? "25px" : isTablet ? "35px" : "50px",
        transition: "all 0.4s ease-in-out",
        "&:hover": {
          transform: "translateY(-12px)",
          "& .feature-title": {
            color: RED,
          },
          "& .icon-diamond": {
            boxShadow: `0 10px 25px rgba(220, 38, 38, 0.5)`,
          },
        },
      }}
    >
      {/* Diamond Icon Background */}
      <Box
        className="icon-diamond"
        sx={{
          width: isMobile ? 70 : isTablet ? 85 : 100,
          height: isMobile ? 70 : isTablet ? 85 : 100,
          backgroundColor: LIGHT_RED_BG,
          margin: "0 auto 15px",
          transform: "rotate(45deg)",
          borderRadius: "8px",
          border: `3px solid ${RED}`,
          boxShadow: `0 5px 15px rgba(0, 0, 0, 0.1)`,
          transition: "all 0.4s ease-in-out",
          position: "relative",
        }}
      >
        <i
          className={`fas fa-${icon}`}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-45deg)",
            fontSize: isMobile ? "1.5rem" : isTablet ? "2rem" : "2.5rem",
            color: DARK_RED_ACCENT,
          }}
        ></i>
      </Box>

      {/* Title and Description */}
      <Typography
        className="feature-title"
        sx={{
          fontWeight: "900",
          color: BLACK_TEXT,
          textTransform: "uppercase",
          marginBottom: isMobile ? "6px" : "8px",
          marginTop: isMobile ? "15px" : "20px",
          transition: "color 0.4s",
          fontSize: isMobile ? "0.75rem" : isTablet ? "0.9rem" : "1.1rem",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          fontSize: isMobile ? "10px" : isTablet ? "11px" : "12px",
          color: GRAY_TEXT,
          lineHeight: 1.5,
          px: isMobile ? "8px" : "0",
        }}
      >
        <Typography
          sx={{
            marginBottom: isMobile ? "3px" : "4px",
            fontSize: "inherit",
            letterSpacing: "0.2px",
          }}
        >
          <strong>{details[0].label}:</strong> {details[0].value}
        </Typography>
        <Typography
          sx={{
            fontSize: "inherit",
            letterSpacing: "0.2px",
          }}
        >
          <strong>{details[1].label}:</strong> {details[1].value}
        </Typography>
      </Box>
    </Box>
  );
};

// Main Component
const WhyChooseAmarEnterprises = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const features = [
    {
      title: "Zero-Compromise Quality",
      icon: "certificate",
      details: [
        {
          label: "Inspection",
          value: "Guaranteed Pre-Export Inspection by certified mechanics.",
        },
        {
          label: "Assurance",
          value: "High-Quality Used Cars selected through rigorous criteria.",
        },
      ],
    },
    {
      title: "Global Shipping Velocity",
      icon: "plane-departure",
      details: [
        {
          label: "Tracking",
          value: "Real-time Vehicle Tracking available until final arrival.",
        },
        {
          label: "Speed",
          value: "Fast Japan Car Delivery via optimized logistics routes.",
        },
      ],
    },
    {
      title: "Unbeatable Value & Pricing",
      icon: "hand-holding-usd",
      details: [
        {
          label: "Pricing",
          value: "Highly Competitive Export Pricing directly from Japan.",
        },
        {
          label: "Affordability",
          value: "Source Budget-Friendly Japanese Cars.",
        },
      ],
    },
    {
      title: "Vast JDM Model Selection",
      icon: "car-side",
      details: [
        {
          label: "Inventory",
          value: "Huge Used Car Inventory updated daily.",
        },
        {
          label: "Variety",
          value: "Wide Selection of rare and popular JDM Models.",
        },
      ],
    },
    {
      title: "Hassle-Free Import Process",
      icon: "file-signature",
      details: [
        {
          label: "Customs",
          value: "Smooth Customs Clearance guidance provided.",
        },
        {
          label: "Support",
          value: "Comprehensive Post-Sale Support and assistance.",
        },
      ],
    },
    {
      title: "Dedicated Export Experts",
      icon: "users-cog",
      details: [
        {
          label: "Service",
          value: "Expert Customer Service available 24/7.",
        },
        {
          label: "Team",
          value: "Dedicated Japanese Export Support specialists.",
        },
      ],
    },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#fff",
      }}
    >
      {/* Header Component */}
      <Box>
        <Notes />
      </Box>

      {/* Banner Section */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
          backgroundColor: "black",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src="/image/AllSeeModelsBackgroungCar.png"
          alt="Automotive Background"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: 0.35,
          }}
        />

        <Typography
          sx={{
            position: "relative",
            color: "white",
            fontWeight: "900",
            textTransform: "uppercase",
            letterSpacing: isMobile ? "1px" : isTablet ? "2px" : "4px",
            fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
            zIndex: 2,
            textShadow: "0 0 10px rgba(0,0,0,0.8)",
            textAlign: "center",
            lineHeight: 1.1,
            px: "20px",
          }}
        >
          The Amar Advantage
        </Typography>
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          position: "relative",
          width: isMobile ? "95%" : isTablet ? "92%" : "85%",
          backgroundColor: "#FFFFFF",
          margin: "0 auto",
          marginTop: isMobile ? "-50px" : isTablet ? "-60px" : "-80px",
          borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
          padding: isMobile ? "20px 16px" : isTablet ? "32px 24px" : "40px 30px",
          boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
          zIndex: 10,
        }}
      >
        {/* Introduction Text */}
        <Box sx={{ textAlign: "center", marginBottom: isMobile ? "35px" : isTablet ? "45px" : "60px" }}>
          <Typography
            sx={{
              fontWeight: "800",
              color: DARK_RED_ACCENT,
              marginBottom: isMobile ? "10px" : "12px",
              textTransform: "uppercase",
              fontSize: isMobile ? "18px" : isTablet ? "24px" : "32px",
              lineHeight: 1.3,
            }}
          >
            Why Choose Amar Enterprises Co Ltd for Japanese Cars?
          </Typography>
          <Typography
            sx={{
              color: GRAY_TEXT,
              fontSize: isMobile ? "13px" : isTablet ? "14px" : "16px",
              maxWidth: "900px",
              margin: "0 auto",
              lineHeight: "1.7",
              px: isMobile ? "12px" : "0",
            }}
          >
            With a <b>17-year legacy of expertise</b> based in <b>Saitama, Japan</b>,
            Amar Enterprises Co Ltd is your trusted source for{" "}
            <b>high-quality, reliable Japanese vehicles</b>. We are dedicated to
            simplifying the import process and delivering exceptional value and
            service globally.
          </Typography>
        </Box>

        {/* Feature Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
            gap: isMobile ? "16px 12px" : isTablet ? "20px 24px" : "24px 30px",
            marginBottom: isMobile ? "25px" : isTablet ? "35px" : "40px",
            px: isMobile ? "0" : isTablet ? "12px" : "20px",
          }}
        >
          {features.map((feature, index) => (
            <DiamondFeature
              key={index}
              title={feature.title}
              icon={feature.icon}
              details={feature.details}
            />
          ))}
        </Box>

        {/* Licensing & CTA Section */}
        <Box
          sx={{
            textAlign: "center",
            marginTop: isMobile ? "25px" : isTablet ? "35px" : "50px",
            padding: isMobile ? "20px 16px" : isTablet ? "28px 24px" : "40px",
            backgroundColor: LIGHT_RED_BG,
            borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
            border: `1px solid ${RED}`,
            boxShadow: `0 0 20px rgba(220, 38, 38, 0.1)`,
          }}
        >
          <Typography
            sx={{
              fontWeight: "800",
              color: RED,
              marginBottom: isMobile ? "12px" : "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              textTransform: "uppercase",
              fontSize: isMobile ? "16px" : isTablet ? "18px" : "22px",
              lineHeight: 1.3,
              flexWrap: "wrap",
            }}
          >
            <i
              className="fas fa-lock"
              style={{
                fontSize: isMobile ? "14px" : "16px",
              }}
            ></i>
            Licensed and Trusted Authority
          </Typography>

          <Typography
            sx={{
              color: BLACK_TEXT,
              fontSize: isMobile ? "13px" : isTablet ? "14px" : "15px",
              marginBottom: isMobile ? "16px" : "20px",
              lineHeight: 1.6,
            }}
          >
            Our operations are fully transparent and compliant with Japanese trade
            regulations, backed by our official license:
          </Typography>

          <Box
            sx={{
              padding: isMobile ? "12px 14px" : isTablet ? "14px 20px" : "15px 30px",
              backgroundColor: "#fff",
              display: "inline-block",
              borderRadius: "10px",
              border: `2px solid ${DARK_RED_ACCENT}`,
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
              marginBottom: isMobile ? "16px" : "20px",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: DARK_RED_ACCENT,
                fontSize: isMobile ? "10px" : isTablet ? "11px" : "13px",
                lineHeight: 1.4,
              }}
            >
              Second Hand Goods Dealer License: Saitama Prefectural Public Safety
              Commission No. 431260012433, Qualification Registration Number: T7-0300-0202-3122
            </Typography>
          </Box>

          {/* CTA Button */}
          <Box sx={{ marginTop: isMobile ? "20px" : isTablet ? "28px" : "40px" }}>
            <a
              href="https://car.amar.co.jp/"
              style={{
                backgroundColor: RED,
                borderColor: RED,
                fontWeight: "900",
                padding: isMobile ? "11px 18px" : isTablet ? "14px 28px" : "18px 40px",
                fontSize: isMobile ? "12px" : isTablet ? "14px" : "18px",
                textTransform: "uppercase",
                boxShadow: `0 6px 15px rgba(220, 38, 38, 0.6)`,
                transition: "background-color 0.3s, transform 0.3s",
                borderRadius: "10px",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "6px" : "12px",
                letterSpacing: "0.5px",
                color: "white",
                textDecoration: "none",
                border: "none",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = DARK_RED_ACCENT;
                e.target.style.transform = "translateY(-3px)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = RED;
                e.target.style.transform = "translateY(0)";
              }}
            >
              <i
                className="fas fa-search-location"
                style={{
                  fontSize: isMobile ? "12px" : "14px",
                }}
              ></i>
              {isMobile ? "FIND CAR" : "FIND YOUR DREAM CAR TODAY!"}
            </a>
          </Box>
        </Box>
      </Box>

      {/* Space Filler */}
      <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />
      {/* Footer */}
      <FooterManual />
    </Box>
  );
};

export default WhyChooseAmarEnterprises;